/*
  Welcome popup — "subscribe and get 10% off your first order".

  Opens once, after a delay or on exit intent (desktop only), whichever comes
  first. Never on load: a popup that greets an ad click before the page has
  rendered is the fastest way to raise bounce rate.

  The signup itself is Shopify's customer form, so a submit reloads the page.
  The section renders the success or error state server-side and sets
  data-state; this script only decides whether to open the dialog.

  Storage is a convenience, not a guarantee. Every read and write is wrapped:
  in a private window it throws, and the popup must still behave (it will
  simply show again on the next visit).
*/
(() => {
  const dialog = document.querySelector('[data-welcome-popup]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const DISMISSED_KEY = 'niro-welcome-dismissed';
  const SUBSCRIBED_KEY = 'niro-welcome-subscribed';
  const SHOWN_KEY = 'niro-welcome-shown';

  const store = {
    get(area, key) {
      try { return window[area].getItem(key); } catch (e) { return null; }
    },
    set(area, key, value) {
      try { window[area].setItem(key, value); } catch (e) { /* storage blocked */ }
    },
  };

  const state = dialog.dataset.state;
  const formId = dialog.dataset.formId;
  const delay = Math.max(0, Number(dialog.dataset.delay) || 0) * 1000;
  const suppressDays = Math.max(0, Number(dialog.dataset.suppressDays) || 0);
  const exitIntent = dialog.dataset.exitIntent === 'true';

  const open = () => {
    if (dialog.open) return;
    dialog.showModal();
    store.set('sessionStorage', SHOWN_KEY, '1');
  };

  // Shopify answers every customer form on the page with the same
  // posted_successfully flag, so a footer signup lands here too. That's fine:
  // anyone who just subscribed should see their code.
  if (state === 'success') {
    store.set('localStorage', SUBSCRIBED_KEY, '1');
    document.documentElement.classList.add('niro-subscribed');
    open();
  } else if (state === 'error' && window.location.hash === `#${formId}`) {
    open();
  }

  const close = () => {
    if (dialog.open) dialog.close();
  };

  dialog.addEventListener('close', () => {
    if (state !== 'success') store.set('localStorage', DISMISSED_KEY, String(Date.now()));
  });

  dialog.querySelectorAll('[data-welcome-close]').forEach((el) => el.addEventListener('click', close));

  // A click on the backdrop lands on the dialog element itself, not its panel.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });

  const copyButton = dialog.querySelector('[data-welcome-copy]');
  if (copyButton && navigator.clipboard) {
    const label = copyButton.textContent;
    copyButton.addEventListener('click', () => {
      navigator.clipboard.writeText(copyButton.dataset.welcomeCopy).then(() => {
        copyButton.textContent = copyButton.dataset.copiedLabel || label;
        setTimeout(() => { copyButton.textContent = label; }, 2000);
      }, () => {});
    });
  } else if (copyButton) {
    copyButton.hidden = true;
  }

  // Any link ending #welcome-offer (the announcement bar's offer line) opens
  // the popup on demand, ignoring the once-per-session and dismissal rules:
  // the shopper asked for it. Elsewhere the link lands on the homepage with the
  // hash, which opens it there.
  const OFFER_HASH = '#welcome-offer';
  document.addEventListener('click', (event) => {
    const link = event.target.closest(`a[href$="${OFFER_HASH}"]`);
    if (!link) return;
    event.preventDefault();
    open();
  });
  if (window.location.hash === OFFER_HASH) open();

  if (state === 'success' || state === 'error') return;

  // Theme editor: open at once and ignore storage, so it can be styled.
  if (dialog.dataset.preview === 'true') {
    dialog.show();
    return;
  }

  // Automatic opening from here on.
  if (store.get('localStorage', SUBSCRIBED_KEY)) {
    // Also hides the cart drawer's "10% off" line for someone already signed up.
    document.documentElement.classList.add('niro-subscribed');
    return;
  }
  if (store.get('sessionStorage', SHOWN_KEY)) return;
  const dismissedAt = Number(store.get('localStorage', DISMISSED_KEY));
  if (dismissedAt && Date.now() - dismissedAt < suppressDays * 864e5) return;

  let armed = true;
  const trigger = () => {
    if (!armed) return;
    // Don't cover a menu, the cart drawer or another modal the shopper opened.
    if (document.querySelector('dialog[open]:not([data-welcome-popup]), details[open] > .menu-drawer, cart-drawer.active')) {
      setTimeout(trigger, 4000);
      return;
    }
    armed = false;
    document.removeEventListener('mouseout', onMouseOut);
    open();
  };

  const onMouseOut = (event) => {
    if (!event.relatedTarget && event.clientY <= 0) trigger();
  };

  setTimeout(trigger, delay);
  if (exitIntent && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.addEventListener('mouseout', onMouseOut);
  }
})();
